import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Email as EmailIcon } from '@mui/icons-material';
import { StakeholderEmail } from '../../types';
import { apiService } from '../../services/api';

interface EmailDialogProps {
  open: boolean;
  email: StakeholderEmail | null;
  onClose: () => void;
  onSave: (email: Partial<StakeholderEmail>) => void;
}

const EmailDialog: React.FC<EmailDialogProps> = ({ open, email, onClose, onSave }) => {
  const [emailAddress, setEmailAddress] = useState('');
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [errors, setErrors] = useState<{ email?: string; name?: string }>({});

  useEffect(() => {
    if (email) {
      setEmailAddress(email.email);
      setName(email.name);
      setActive(email.active);
    } else {
      setEmailAddress('');
      setName('');
      setActive(true);
    }
    setErrors({});
  }, [email, open]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors: { email?: string; name?: string } = {};
    
    if (!emailAddress.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(emailAddress.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    onSave({
      id: email?.id,
      email: emailAddress.trim().toLowerCase(),
      name: name.trim(),
      active
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {email ? 'Edit Stakeholder Email' : 'Add Stakeholder Email'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            margin="normal"
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {email ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StakeholderEmailsConfig: React.FC = () => {
  const [emails, setEmails] = useState<StakeholderEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState<StakeholderEmail | null>(null);

  const loadEmails = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Loading stakeholder emails...');
      const response = await apiService.getStakeholderEmails();
      
      console.log('📡 Full API response:', response);
      console.log('📊 Response data:', response?.data);

      if (response?.error) {
        console.log('❌ Error details:', response.error);
        setError(response.error.error.message);
        setEmails([]);
      } else {
        // The backend returns: { success: true, data: StakeholderEmail[], timestamp, requestId }
        // So the actual emails array is at response.data.data
        let emailsData: StakeholderEmail[] = [];
        
        if (response?.data) {
          const responseData = response.data as any; // Type assertion to handle nested structure
          
          if (Array.isArray(responseData)) {
            // Direct array response
            console.log('✅ Data is direct array:', responseData);
            emailsData = responseData;
          } else if (responseData.data && Array.isArray(responseData.data)) {
            // Backend response format: { success: true, data: StakeholderEmail[] }
            console.log('✅ Data is in response.data.data:', responseData.data);
            emailsData = responseData.data;
          } else {
            console.log('⚠️ Unexpected data structure:', responseData);
            emailsData = [];
          }
        } else {
          console.log('⚠️ No data in response');
        }
        
        console.log('📋 Final emails data:', emailsData);
        console.log('📏 Emails array length:', emailsData.length);
        setEmails(Array.isArray(emailsData) ? emailsData : []);
      }
    } catch (error) {
      console.error('💥 Error loading stakeholder emails:', error);
      setError('Unable to load stakeholder emails. Please try again.');
      setEmails([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadEmails();
  }, []);

  const handleSaveEmails = async (updatedEmails: StakeholderEmail[]) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('💾 Saving stakeholder emails:', updatedEmails);
      const response = await apiService.updateStakeholderEmails(updatedEmails);
      
      console.log('📡 Save response:', response);

      if (response?.error) {
        console.log('❌ Save error:', response.error);
        setError(response.error.error.message);
      } else {
        console.log('✅ Save successful, refreshing email list...');
        
        // Always refresh the email list from the server after a successful save
        // This ensures we have the latest data and handles all response formats
        await loadEmails();
        
        setSuccess('Stakeholder emails updated successfully!');
      }
    } catch (error) {
      console.error('💥 Error saving stakeholder emails:', error);
      setError('Unable to save stakeholder emails. Please try again.');
    }

    setSaving(false);
  };

  // Email validation helper
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check for duplicate emails
  const isDuplicateEmail = (email: string, excludeId?: string): boolean => {
    return emails.some(existingEmail => 
      existingEmail.email.toLowerCase() === email.toLowerCase() && 
      existingEmail.id !== excludeId
    );
  };

  const handleAddEmail = () => {
    setEditingEmail(null);
    setDialogOpen(true);
  };

  const handleEditEmail = (email: StakeholderEmail) => {
    setEditingEmail(email);
    setDialogOpen(true);
  };

  const handleDeleteEmail = async (emailId: string) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Find the email to delete
      const emailToDelete = emails.find(e => e.id === emailId);
      if (!emailToDelete) {
        setError('Email not found');
        return;
      }

      console.log('🗑️ Deleting email:', emailToDelete.email);
      
      // Send delete request with action: 'delete'
      const deleteRequest = [{
        email: emailToDelete.email,
        action: 'delete' as const
      }];
      
      const response = await apiService.updateStakeholderEmails(deleteRequest);
      
      console.log('📡 Delete response:', response);

      if (response?.error) {
        console.log('❌ Delete error:', response.error);
        setError(response.error.error.message);
      } else {
        console.log('✅ Delete successful, refreshing email list...');
        
        // Refresh the email list from the server
        await loadEmails();
        
        setSuccess('Email deleted successfully!');
      }
    } catch (error) {
      console.error('💥 Error deleting email:', error);
      setError('Failed to delete email. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDialogSave = (emailData: Partial<StakeholderEmail>) => {
    // Validate required fields
    if (!emailData.name || !emailData.email) {
      setError('Name and email are required');
      return;
    }

    // Validate email format
    if (!validateEmail(emailData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check for duplicate email addresses
    if (isDuplicateEmail(emailData.email, editingEmail?.id)) {
      setError('This email address is already configured');
      return;
    }

    let updatedEmails: StakeholderEmail[];

    if (editingEmail) {
      // Update existing email
      updatedEmails = emails.map(e => 
        e.id === editingEmail.id 
          ? { ...e, ...emailData }
          : e
      );
    } else {
      // Add new email - don't include ID, let backend generate it from email address
      const newEmail = {
        email: emailData.email!,
        name: emailData.name!,
        active: emailData.active ?? true,
        createdAt: new Date()
      };
      updatedEmails = [...emails, newEmail as StakeholderEmail];
    }

    handleSaveEmails(updatedEmails);
    setDialogOpen(false);
  };

  // Bulk actions
  const handleBulkActivate = () => {
    const updatedEmails = emails.map(e => ({ ...e, active: true }));
    handleSaveEmails(updatedEmails);
  };

  const handleBulkDeactivate = () => {
    const updatedEmails = emails.map(e => ({ ...e, active: false }));
    handleSaveEmails(updatedEmails);
  };

  const handleToggleActive = (emailId: string) => {
    const updatedEmails = emails.map(e => 
      e.id === emailId 
        ? { ...e, active: !e.active }
        : e
    );
    handleSaveEmails(updatedEmails);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const activeEmails = emails.filter(e => e.active);
  const inactiveEmails = emails.filter(e => !e.active);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Stakeholder Email Configuration
        </Typography>
        <Box display="flex" gap={1}>
          {emails.length > 0 && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleBulkActivate}
                disabled={saving}
              >
                Activate All
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleBulkDeactivate}
                disabled={saving}
              >
                Deactivate All
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEmail}
            disabled={saving}
          >
            Add Email
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Box mb={3}>
        <Typography variant="body2" color="text.secondary">
          Active emails: <strong>{activeEmails.length}</strong> | 
          Inactive emails: <strong>{emails.length - activeEmails.length}</strong> | 
          Total emails: <strong>{emails.length}</strong>
        </Typography>
        {activeEmails.length === 0 && emails.length > 0 && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            ⚠️ No active emails configured. Reports will not be distributed.
          </Typography>
        )}
      </Box>

      <Grid container spacing={2}>
        {emails.map((email) => (
          <Grid item xs={12} md={6} key={email.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" component="h3">
                      {email.name}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={1}>
                      <EmailIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {email.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={email.active ? 'Active' : 'Inactive'}
                    color={email.active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary">
                  Added: {formatDate(email.createdAt)}
                </Typography>
              </CardContent>
              
              <CardActions>
                <FormControlLabel
                  control={
                    <Switch
                      checked={email.active}
                      onChange={() => handleToggleActive(email.id)}
                      disabled={saving}
                      size="small"
                    />
                  }
                  label="Active"
                />
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  onClick={() => handleEditEmail(email)}
                  disabled={saving}
                  size="small"
                  aria-label="Edit"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteEmail(email.id)}
                  disabled={saving}
                  size="small"
                  color="error"
                  aria-label="Delete"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {emails.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No stakeholder emails configured. Add your first email to get started.
          </Typography>
        </Box>
      )}

      <EmailDialog
        open={dialogOpen}
        email={editingEmail}
        onClose={() => setDialogOpen(false)}
        onSave={handleDialogSave}
      />
    </Box>
  );
};

export default StakeholderEmailsConfig;